import * as React from 'react';
import { getFindGameUrl } from 'constants/pages';
import { NotificationActionTypesEnum, NotificationStatusesEnum } from 'types/generated/client';
import Link from 'components/Link';
import NotificationActivityCard, {
  NotificationGameCard,
  type NotificationPillTone,
} from './NotificationActivityCard';
import { NotificationTypeSelectorProps } from '../types';

export type GameInviteActionType =
  | NotificationActionTypesEnum.GameInviteReceived
  | NotificationActionTypesEnum.GameInviteAccepted
  | NotificationActionTypesEnum.GameInviteDeclined;

const VARIANTS: Record<
  GameInviteActionType,
  { pill: { label: string; tone: NotificationPillTone }; title: string }
> = {
  [NotificationActionTypesEnum.GameInviteReceived]: {
    pill: { label: 'Game Invite', tone: 'received' },
    title: 'Invited you to their game',
  },
  [NotificationActionTypesEnum.GameInviteAccepted]: {
    pill: { label: 'Invite Accepted', tone: 'accepted' },
    title: 'Accepted your game invite',
  },
  [NotificationActionTypesEnum.GameInviteDeclined]: {
    pill: { label: 'Invite Declined', tone: 'declined' },
    title: 'Declined your game invite',
  },
};

interface Props extends NotificationTypeSelectorProps {
  status: NotificationStatusesEnum;
  actionType: GameInviteActionType;
}

/**
 * A game invite sent, accepted or declined. All three link straight to the game's
 * detail sheet on /find — for the invited player that is where Accept/Decline
 * live, and for the host it is where the roster is.
 */
export default function GameInviteNotification({ notification, status, actionType }: Props) {
  const primaryEntity = notification?.notificationDetails?.primaryEntity;
  const invite = primaryEntity?.eventPlayerInvite;
  const event = invite?.event;

  const content = (
    <NotificationActivityCard
      status={status}
      actorName={primaryEntity?.actingUserProfile?.fullName ?? 'A player'}
      actorCoachStatus={primaryEntity?.actingUserProfile?.coachStatus}
      {...VARIANTS[actionType]}
    >
      {event && <NotificationGameCard event={event} />}
    </NotificationActivityCard>
  );

  if (!invite?.eventId) {
    return content;
  }

  return (
    <Link href={getFindGameUrl(invite.eventId)} className="block">
      {content}
    </Link>
  );
}
